import React, { useState, useEffect, useRef } from 'react';
import AuthModal from './AuthModal';
import { useReveal } from './hooks/useReveal';
import heroImg from './assets/hero.png';
import heroWebp from './assets/hero.webp';
import heroMobileWebp from './assets/hero-mobile.webp';
import { PolicyPage, PRIVACY, TERMS, DISCLAIMER } from './Profile';
import DeleteData from './DeleteData';

const isNativePlatform = () => { try { return window.Capacitor && window.Capacitor.isNativePlatform(); } catch { return false; } };

function AnimatedStat({ target, suffix, label }) {
  const [ref, visible] = useReveal(0.3);
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!visible || hasRun.current) return;
    hasRun.current = true;
    const duration = 2000;
    const start = performance.now();
    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [visible, target]);

  return (
    <div ref={ref} style={{ textAlign: 'center', padding: '0 16px' }}>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(36px, 4vw, 52px)',
        color: '#C9973A',
        fontWeight: 500,
        lineHeight: 1,
        borderBottom: '1px solid rgba(201,151,58,0.4)',
        paddingBottom: 12,
        marginBottom: 10,
      }}>
        {count}{suffix}
      </div>
      <div style={{ fontSize: 14, color: 'var(--white-60)', lineHeight: 1.4 }}>{label}</div>
    </div>
  );
}

function RevealWrap({ children, delay, style, className }) {
  const [ref, visible] = useReveal();
  const delayClass = delay ? ` reveal-delay-${delay}` : '';
  const extraClass = className ? ` ${className}` : '';
  return (
    <div ref={ref} className={`reveal${visible ? ' visible' : ''}${delayClass}${extraClass}`} style={style}>
      {children}
    </div>
  );
}

export default function Landing({ setPage, showLogin = false }) {
  const [showModal, setShowModal] = useState(showLogin);
  const [modalMode, setModalMode] = useState('login');
  const [openFaq, setOpenFaq] = useState(null);
  const [openPolicy, setOpenPolicy] = useState(null);
  const [showDeleteData, setShowDeleteData] = useState(false);

  useEffect(() => {
    if (showLogin) { setShowModal(true); setModalMode('login'); }
  }, [showLogin]);

  const categories = [
    { icon: '⚡', label: 'Stress & Anxiety', sub: 'Calm your mind' },
    { icon: '🌙', label: 'Sleep & Rest', sub: 'Find peaceful sleep' },
    { icon: '💰', label: 'Abundance', sub: 'Release money blocks' },
    { icon: '💛', label: 'Relationships', sub: 'Heal connections' },
    { icon: '⭐', label: 'Confidence', sub: 'Unlock self-worth' },
    { icon: '🛡️', label: 'Fear & Overwhelm', sub: 'Restore your calm' },
    { icon: '🫀', label: 'Physical Health', sub: 'Body & healing' },
    { icon: '✏️', label: 'Something Else', sub: 'Your unique issue' },
  ];

  const styles = [
    { name: 'Classic EFT', desc: 'Traditional acknowledgment & acceptance' },
    { name: 'Positive Focus', desc: 'Uplifting, solution-oriented language' },
    { name: 'Inner Child', desc: 'Nurturing, gentle healing' },
    { name: 'Body-Focused', desc: 'Physical sensations & grounding' },
    { name: 'Spiritual & Intuitive', desc: 'Soul-centered, higher wisdom' },
    { name: 'Logical & Grounded', desc: 'Practical, no-nonsense approach' },
  ];

  const painPoints = [
    'Do you struggle to find the right words when you tap?',
    'Does your mind go blank the moment you sit down?',
    'Are you tired of generic scripts that don\'t fit your situation?',
    'Do you want to tap more but don\'t know where to start?',
  ];

  const trustPoints = [
    'Designed by a certified EFT practitioner & educator with 10+ years of experience',
    'Based on clinical EFT research',
    'Your privacy is protected. We never share your information.',
  ];

  const faqs = [
    {
      q: 'What is EFT Tapping?',
      a: 'EFT (Emotional Freedom Techniques) is a research-backed self-help method that combines gentle tapping on acupressure points with spoken phrases to release emotional stress, anxiety, and negative beliefs. It\'s simple, effective, and you can do it anywhere.',
    },
    {
      q: 'Do I need to know EFT to use this app?',
      a: 'Not at all. My Tapping Script guides you through everything. You describe what you\'re going through, the app creates your script, and you simply tap along. A Learn EFT section inside the app walks you through the basics if you\'re new.',
    },
    {
      q: 'How is this different from generic EFT scripts online?',
      a: 'Generic scripts are written for everyone, which means they\'re written for no one in particular. My Tapping Script asks you about your specific emotions, where you feel them in your body, what\'s underneath them, and creates a script using your own words and situation.',
    },
    {
      q: 'How long does a tapping session take?',
      a: 'Most scripts take 10-15 minutes to tap through. You can listen along using the built-in audio or read the script yourself. Download it as a PDF to print and use anytime.',
    },
    {
      q: 'Is EFT backed by research?',
      a: 'Yes. There is a growing body of clinical research supporting EFT for stress, anxiety, PTSD, phobias, and emotional regulation. Studies have shown measurable reductions in cortisol levels after EFT sessions.',
    },
    {
      q: 'What do I get with the free trial?',
      a: '7 free personalised tapping scripts over 7 days. No credit card required. Each script is fully generated, downloadable as a PDF, and includes audio playback. After your trial, subscribe for unlimited scripts at $5.99/month.',
    },
    {
      q: 'Who built My Tapping Script?',
      a: 'My Tapping Script was built by a small team composed of an EFT expert and educator, Michael Hetherington (BHlthSc), and Tech Developer Chris from the UK. After seeing his EFT clients and students consistently struggle with finding the right phrasing and words, Michael decided to create this software, aiming to empower people with their EFT practice and enhance their healing process.',
    },
  ];

  if (openPolicy === 'privacy') return <PolicyPage title="Privacy Policy" onBack={() => setOpenPolicy(null)} content={PRIVACY} />;
  if (openPolicy === 'terms') return <PolicyPage title="Terms of Service" onBack={() => setOpenPolicy(null)} content={TERMS} />;
  if (openPolicy === 'disclaimer') return <PolicyPage title="Disclaimer" onBack={() => setOpenPolicy(null)} content={DISCLAIMER} />;
  if (showDeleteData) return <DeleteData onBack={() => setShowDeleteData(false)} />;

  return (
    <div className="main-page" style={{ paddingTop: 'calc(64px + env(safe-area-inset-top, 0px))' }}>
      {showModal && (
        <AuthModal
          mode={modalMode}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); setPage('home'); }}
        />
      )}

      {/* ── HERO ── */}
      <section className="landing-hero-section" style={{
        minHeight: '92vh', display: 'flex', alignItems: 'center',
        background: 'radial-gradient(ellipse at 50% 50%, rgba(212,168,67,0.08) 0%, transparent 60%)',
        padding: '60px 0',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div className="hero-blob hero-blob-1" />
        <div className="hero-blob hero-blob-2" />
        <div className="page-container" style={{ width: '100%', position: 'relative', zIndex: 1 }}>

          {/* Two column */}
          <div className="grid-1-on-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center', marginBottom: 48 }}>
            <div>
              {/* Main headline */}
              <div className="hero-enter-1">
                <h1 className="landing-hero-h1" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 66px)', fontWeight: 400, lineHeight: 1.1, marginBottom: 20 }}>
                  Your script.<br />
                  <span className="hero-gold-shimmer">Your healing.</span>
                </h1>
              </div>

              {/* Value prop */}
              <div className="hero-enter-2">
                <p className="landing-hero-body" style={{ fontSize: 'clamp(15px, 2vw, 17px)', color: 'var(--white-80)', lineHeight: 1.8, marginBottom: 12 }}>
                  EFT Tapping is a proven stress-relief technique. But most people struggle to find the right words for their situation, blocking their healing process.
                </p>
                <p className="landing-hero-body" style={{ fontSize: 'clamp(15px, 2vw, 17px)', color: 'var(--white)', lineHeight: 1.8, marginBottom: 28 }}>
                  <span style={{ color: 'var(--gold)', fontWeight: 600 }}>My Tapping Script</span> writes a personalised script for exactly what you're going through... in seconds.
                </p>
              </div>

              {/* CTA */}
              <div className="hero-enter-3">
                <button className="btn-primary hero-cta-pulse" style={{ fontSize: 17, marginBottom: 10 }} onClick={() => setPage('create')}>
                  Create Your Free Script →
                </button>
                <p style={{ color: 'var(--gold)', fontWeight: 700, fontSize: 16, textAlign: 'center', marginBottom: 14 }}>
                  7 Free Scripts · 7 Days · No Credit Card Needed
                </p>
              </div>

              {/* Android CTA — web only */}
              {!isNativePlatform() && (
                <div className="hero-enter-4" style={{ textAlign: 'center', marginBottom: 14 }}>
                  <a href="https://play.google.com/store/apps/details?id=com.mytappingscript.app" target="_blank" rel="noreferrer" style={{ textDecoration: 'none', display: 'inline-block' }}>
                    <button style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      background: 'transparent', border: '1px solid var(--gold-20)',
                      color: 'var(--white-80)', borderRadius: 10, padding: '11px 22px',
                      fontSize: 15, fontWeight: 600, cursor: 'pointer',
                      transition: 'border-color 0.2s, color 0.2s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--white)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gold-20)'; e.currentTarget.style.color = 'var(--white-80)'; }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.18 23.76c.3.17.64.24.98.2L15.54 12 3.18.04A1.5 1.5 0 003 .76v22.22c0 .28.07.55.18.78z" fill="#EA4335"/>
                        <path d="M19.84 15.6l-3.1-1.78L13.42 12l3.32-1.82 3.1-1.78 2.02 1.22c.58.35.92.96.92 1.58s-.34 1.23-.92 1.58l-2.02 1.82z" fill="#FBBC04"/>
                        <path d="M3.18.04l12.36 11.78L19.84 8.4l-14.2-8.12A1.5 1.5 0 003.18.04z" fill="#4285F4"/>
                        <path d="M3.18 23.96A1.5 1.5 0 005.64 23.72l14.2-8.12-4.3-3.42L3.18 23.96z" fill="#34A853"/>
                      </svg>
                      App Now Available at Google Play
                    </button>
                  </a>
                </div>
              )}

              {/* Testimonials */}
              <div className="hero-enter-5 grid-1-on-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {[
                  { quote: 'I went from a 7 down to a 3. My heart rate decreased too. Will definitely be using this again.', name: 'Mary K.', country: 'USA' },
                  { quote: 'I went from an 8 to a 1. The situation has stopped invading my thoughts.', name: 'K. Huedepohl', country: 'India' },
                  { quote: 'I went from an 8 to a 4. Thank you for this.', name: 'Catherine W.', country: 'UK' },
                ].map((t, i) => (
                  <div key={i} style={{ background: 'var(--navy-card)', border: '1px solid var(--white-10)', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ color: 'var(--gold)', fontSize: 13, marginBottom: 6, letterSpacing: 2 }}>★★★★★</div>
                    <p style={{ fontSize: 13, color: 'var(--white-80)', lineHeight: 1.6, marginBottom: 8, fontStyle: 'italic' }}>"{t.quote}"</p>
                    <p style={{ fontSize: 12, color: 'var(--white-60)', fontWeight: 600 }}>{t.name}, {t.country}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="landing-hero-image hero-enter-img" style={{ textAlign: 'center' }}>
              <div className="hero-image-float" style={{ width: '100%', maxWidth: 480, borderRadius: 24, overflow: 'hidden', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
                <picture>
                  <source media="(max-width: 600px)" srcSet={heroMobileWebp} type="image/webp" />
                  <source srcSet={heroWebp} type="image/webp" />
                  <img src={heroImg} alt="EFT Tapping" fetchpriority="high" loading="eager" style={{ width: '100%', height: 420, objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
                </picture>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 50%, var(--navy) 100%)' }} />
              </div>
            </div>
          </div>

          {/* Trust banner */}
          <div className="hero-enter-trust" style={{
            background: 'var(--navy-card)', border: '1px solid var(--gold-20)',
            borderRadius: 16, padding: '20px 24px',
            display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 24,
          }}>
            {[
              { icon: '🏆', text: '10+ Years Experience', bounceClass: 'hero-icon-bounce-1' },
              { icon: '🔬', text: 'Evidence Based',        bounceClass: 'hero-icon-bounce-2' },
              { icon: '🔒', text: 'Private & Secure',      bounceClass: 'hero-icon-bounce-3' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span className={t.bounceClass} style={{ fontSize: 32 }}>{t.icon}</span>
                <p style={{ fontSize: 15, color: 'var(--white-80)', fontWeight: 600 }}>{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY IT WORKS ── */}
      <section style={{ padding: '56px 0', background: 'var(--navy-card)', borderTop: '1px solid var(--gold-20)', borderBottom: '1px solid var(--gold-20)' }}>
        <div className="page-container" style={{ maxWidth: 720 }}>
          <div style={{ textAlign: 'center' }}>
            <RevealWrap>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 3vw, 36px)', marginBottom: 16, color: 'var(--white)' }}>
                Is EFT Tapping Actually Real?
              </h2>
            </RevealWrap>
            <p style={{ fontSize: 'clamp(15px, 2vw, 17px)', color: 'var(--white-80)', lineHeight: 1.9, maxWidth: 660, margin: '0 auto 20px' }}>
              If you're skeptical, that's completely fair. EFT is not new-age guesswork. It has been studied by more than 200 researchers across 12 countries, with 275 studies published in peer-reviewed scientific journals, including 70 randomised controlled trials. The results consistently show real reductions in stress, anxiety, PTSD and emotional distress.
            </p>
            <p style={{ fontSize: 'clamp(14px, 1.8vw, 16px)', color: 'var(--white-60)', lineHeight: 1.7, maxWidth: 560, margin: '0 auto 24px' }}>
              It works by gently tapping on acupressure points on the body while speaking about what's bothering you. This combination calms your nervous system and helps release the emotional charge around the issue.
            </p>

            {/* Animated stats */}
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 16,
              padding: 40,
              margin: '32px auto',
              maxWidth: 700,
            }}>
              <div className="stats-grid">
                <AnimatedStat target={275} suffix="" label="peer-reviewed studies" />
                <AnimatedStat target={200} suffix="+" label="researchers worldwide" />
                <AnimatedStat target={70} suffix="" label="randomised controlled trials" />
                <AnimatedStat target={12} suffix="" label="countries with research" />
              </div>
            </div>

            <p style={{ fontSize: 14, color: 'var(--white-60)', marginBottom: 16 }}>Here are some studies you can check out:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 600, margin: '0 auto' }}>
              <RevealWrap delay={1}>
                <a href="https://journals.sagepub.com/doi/full/10.1177/2515690X18823691" target="_blank" rel="noreferrer"
                  style={{ background: 'rgba(212,168,67,0.08)', border: '1px solid var(--gold-20)', borderRadius: 10, padding: '14px 18px', textDecoration: 'none', display: 'block', textAlign: 'center' }}>
                  <p style={{ fontSize: 12, color: 'var(--gold)', fontWeight: 600, marginBottom: 4 }}>Journal of Evidence-Based Integrative Medicine</p>
                  <p style={{ fontSize: 13, color: 'var(--white-80)', lineHeight: 1.5 }}>Clinical EFT Improves Multiple Physiological Markers of Health <span style={{ color: 'var(--gold)' }}>→</span></p>
                </a>
              </RevealWrap>
              <RevealWrap delay={2}>
                <a href="https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2023.1195286/full?lid=16z38ysw0550" target="_blank" rel="noreferrer"
                  style={{ background: 'rgba(212,168,67,0.08)', border: '1px solid var(--gold-20)', borderRadius: 10, padding: '14px 18px', textDecoration: 'none', display: 'block', textAlign: 'center' }}>
                  <p style={{ fontSize: 12, color: 'var(--gold)', fontWeight: 600, marginBottom: 4 }}>Frontiers in Psychology Journal</p>
                  <p style={{ fontSize: 13, color: 'var(--white-80)', lineHeight: 1.5 }}>Emotional Freedom Techniques for Treating Post-Traumatic Stress Disorder <span style={{ color: 'var(--gold)' }}>→</span></p>
                </a>
              </RevealWrap>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO IS THIS FOR ── */}
      <section style={{ padding: '80px 0', background: '#0F2240' }}>
        <div className="page-container">
          <RevealWrap>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <h2 className="section-title">Who Is This For?</h2>
              <div className="divider" style={{ margin: '16px auto' }} />
            </div>
          </RevealWrap>
          <div className="grid-1-on-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {[
              {
                icon: '🌱',
                title: 'EFT Beginners',
                desc: "You've heard about tapping and want to try it but you freeze when it comes to the words.",
              },
              {
                icon: '💆',
                title: 'Regular Tappers',
                desc: 'You tap often but find yourself repeating the same phrases.',
              },
              {
                icon: '🧑‍⚕️',
                title: 'EFT Practitioners',
                desc: 'Use My Tapping Script to quickly generate personalised scripts for your clients between sessions.',
              },
              {
                icon: '🌿',
                title: 'Natural Therapists & Healers',
                desc: "Whether you're an acupuncturist, naturopath, or holistic therapist, add EFT to your toolkit.",
              },
              {
                icon: '🧠',
                title: 'Mental Health Professionals',
                desc: "Support your clients with evidence-based EFT scripts between sessions.",
              },
              {
                icon: '👨‍👩‍👧',
                title: 'Parents & Educators',
                desc: 'Help children and young people process big emotions with language they can actually relate to.',
              },
            ].map((card, i) => (
              <RevealWrap key={card.title} delay={(i % 3) + 1}>
                <div className="card-border-hover" style={{
                  background: 'var(--navy)', border: '1px solid var(--white-10)',
                  borderRadius: 16, padding: '28px 24px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 36, marginBottom: 16 }}>{card.icon}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 21, marginBottom: 12, color: 'var(--white)' }}>{card.title}</h3>
                  <p style={{ fontSize: 15, color: 'var(--white-60)', lineHeight: 1.75 }}>{card.desc}</p>
                </div>
              </RevealWrap>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <button className="btn-primary" style={{ width: 'auto', padding: '16px 40px', fontSize: 16 }} onClick={() => setPage('create')}>
              Create Your Free Script
            </button>
            <p style={{ marginTop: 14, fontSize: 13, color: 'var(--white-60)' }}>
              7 free scripts over 7 days · No credit card needed
            </p>
          </div>
        </div>
      </section>

      {/* wave: #0F2240 → #0B1A2E */}
      <div style={{ background: '#0F2240', lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 60 }}>
          <path d="M0,0 C480,60 960,0 1440,40 L1440,60 L0,60 Z" fill="#0B1A2E" />
        </svg>
      </div>

      {/* ── WHAT TO WORK ON ── */}
      <section style={{ padding: '80px 0', background: 'var(--navy)' }}>
        <div className="page-container">
          <RevealWrap>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 className="section-title">What would you like<br />to work on today?</h2>
              <div className="divider" style={{ margin: '16px auto' }} />
              <p style={{ color: 'var(--white-60)', fontSize: 16 }}>Choose an area and receive your personalised script in seconds</p>
            </div>
          </RevealWrap>
          <div className="grid-2-on-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {categories.map((c, i) => (
              <RevealWrap key={c.label} delay={(i % 3) + 1}>
                <div className="card topic-card" onClick={() => setPage('create')}
                  style={{ textAlign: 'center', padding: '28px 20px' }}>
                  <span className="topic-icon" style={{ fontSize: 32, marginBottom: 12, display: 'block' }}>{c.icon}</span>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--white-60)' }}>{c.sub}</div>
                </div>
              </RevealWrap>
            ))}
          </div>
        </div>
      </section>

      {/* wave: #0B1A2E → #0F2240 */}
      <div style={{ background: '#0B1A2E', lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 60 }}>
          <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#0F2240" />
        </svg>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: '80px 0', background: '#0F2240' }}>
        <div className="page-container">
          <RevealWrap>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <h2 className="section-title">How It Works</h2>
              <div className="divider" style={{ margin: '16px auto' }} />
            </div>
          </RevealWrap>
          <div className="grid-1-on-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32, marginBottom: 40 }}>
            {[
              { step: '01', title: "Tell us what's bothering you", desc: "Choose your focus area, describe your emotions, and share what's weighing on your mind." },
              { step: '02', title: 'Receive your personalised script', desc: 'Your unique EFT tapping script is created instantly, tailored to your exact words and situation.' },
              { step: '03', title: 'Tap along & feel the shift', desc: 'Listen along using the built-in audio, or download your script as a PDF to use whenever you need it.' },
            ].map((s, i) => (
              <RevealWrap key={s.step} delay={i + 1}>
                <div className="card card-border-hover" style={{ padding: 32 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 48, color: 'var(--gold)', opacity: 0.4, lineHeight: 1, marginBottom: 16 }}>{s.step}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 12 }}>{s.title}</h3>
                  <p style={{ color: 'var(--white-60)', lineHeight: 1.6 }}>{s.desc}</p>
                </div>
              </RevealWrap>
            ))}
          </div>

          {/* Personalisation differentiator */}
          <RevealWrap>
            <div style={{ background: 'linear-gradient(135deg, rgba(212,168,67,0.12), rgba(212,168,67,0.04))', border: '1px solid var(--gold-20)', borderRadius: 16, padding: '28px 32px', textAlign: 'center' }}>
              <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'var(--white)', lineHeight: 1.8, fontWeight: 500 }}>
                💡 <span style={{ color: 'var(--gold)', fontWeight: 700 }}>Generic scripts don't work</span> because they weren't written for YOUR issue.{' '}
                Every script here is generated specifically for what <em>you</em> are going through right now.
              </p>
            </div>
          </RevealWrap>
        </div>
      </section>

      {/* wave: #0F2240 → #0B1A2E */}
      <div style={{ background: '#0F2240', lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 60 }}>
          <path d="M0,10 C360,60 1080,0 1440,45 L1440,60 L0,60 Z" fill="#0B1A2E" />
        </svg>
      </div>

      {/* ── COMPETITOR DIFFERENTIATION ── */}
      <section style={{ padding: '80px 0', background: 'var(--navy)' }}>
        <div className="page-container" style={{ maxWidth: 720 }}>
          <RevealWrap>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--gold-20)', borderRadius: 20, padding: '40px 36px', textAlign: 'center' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 3vw, 32px)', marginBottom: 20, color: 'var(--white)', lineHeight: 1.3 }}>
              Why Not Just Use Another Tapping App?<br />Why Not Just Use YouTube Videos?
            </h2>
            <p style={{ fontSize: 'clamp(15px, 2vw, 17px)', color: 'var(--white-70)', lineHeight: 1.9, marginBottom: 24, maxWidth: 580, margin: '0 auto 24px' }}>
              Other tapping apps and YouTube videos give you pre-written scripts that were made for everyone — which means they were really made for no one. My Tapping Script writes your script from scratch, based on exactly what you typed, every single time.
            </p>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 700, lineHeight: 1.8 }}>
              <span style={{ color: 'var(--gold)' }}>Your words. Your situation. Your healing.</span>
            </p>
          </div>
          </RevealWrap>
        </div>
      </section>

      {/* wave: #0F2240 → #0B1A2E → #0F2240 (competitor is #0B1A2E, styles is #0F2240) */}
      <div style={{ background: '#0B1A2E', lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 60 }}>
          <path d="M0,20 C400,70 1000,0 1440,35 L1440,60 L0,60 Z" fill="#0F2240" />
        </svg>
      </div>

      {/* ── SCRIPT STYLES ── */}
      <section style={{ padding: '80px 0', background: '#0F2240' }}>
        <div className="page-container">
          <RevealWrap>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 className="section-title">Choose Your Style</h2>
              <div className="divider" style={{ margin: '16px auto' }} />
              <p style={{ color: 'var(--white-60)' }}>Every script adapts to how you want to feel</p>
            </div>
          </RevealWrap>
          <div className="grid-1-on-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {styles.map((s, i) => (
              <RevealWrap key={s.name} delay={(i % 3) + 1}>
                <div className="card style-card" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span className="style-plus" style={{ background: i === 0 ? 'var(--gold)' : 'var(--white-10)', color: i === 0 ? 'var(--navy)' : 'var(--white-60)' }}>✦</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--white-60)', marginTop: 2 }}>{s.desc}</div>
                  </div>
                </div>
              </RevealWrap>
            ))}
          </div>
        </div>
      </section>

      {/* wave: #0F2240 → #0B1A2E */}
      <div style={{ background: '#0F2240', lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 60 }}>
          <path d="M0,40 C500,0 940,60 1440,20 L1440,60 L0,60 Z" fill="#0B1A2E" />
        </svg>
      </div>

      {/* ── MAIN CTA ── */}
      <section style={{ padding: '80px 0', textAlign: 'center' }}>
        <div className="page-container" style={{ maxWidth: 600 }}>
          <RevealWrap>
            <h2 className="section-title" style={{ marginBottom: 16 }}>Ready to feel better?</h2>
          </RevealWrap>
          <p style={{ color: 'var(--white-60)', marginBottom: 32, fontSize: 16 }}>
            Join thousands of people using personalised EFT scripts to release stress, anxiety, and emotional overwhelm.
          </p>
          <button className="btn-primary" style={{ width: 'auto', padding: '18px 48px', fontSize: 17 }} onClick={() => setPage('create')}>
            Create My Free Script
          </button>
          <p style={{ marginTop: 16, fontSize: 13, color: 'var(--white-60)' }}>7 free scripts · No credit card needed · Cancel anytime</p>
        </div>
      </section>

      {/* ── PAIN POINTS ── */}
      <section style={{ padding: '80px 0', background: 'var(--navy-light)' }}>
        <div className="page-container" style={{ maxWidth: 720 }}>
          <RevealWrap>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 3vw, 42px)', marginBottom: 16, lineHeight: 1.2 }}>
                You know EFT works.<br />
                <span style={{ color: 'var(--gold)' }}>But what do you actually say?</span>
              </h2>
              <p style={{ color: 'var(--white-60)', fontSize: 16, maxWidth: 560, margin: '0 auto' }}>
                You're not alone. These are the most common struggles people have with EFT tapping.
              </p>
            </div>
          </RevealWrap>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 40 }}>
            {painPoints.map((pain, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 16,
                background: 'var(--navy-card)', border: '1px solid var(--white-10)',
                borderRadius: 12, padding: '20px 24px',
              }}>
                <span style={{ color: 'var(--gold)', fontSize: 22, flexShrink: 0 }}>✦</span>
                <p style={{ fontSize: 17, color: 'var(--white-80)', lineHeight: 1.5 }}>{pain}</p>
              </div>
            ))}
          </div>

          {/* Closing statement */}
          <div style={{
            background: 'var(--navy-card)', border: '1px solid var(--gold-20)',
            borderRadius: 16, padding: '28px 32px', textAlign: 'center', marginBottom: 32,
          }}>
            <p style={{ fontSize: 17, color: 'var(--white-80)', lineHeight: 1.8 }}>
              Many people get stuck with the statements and words, and this stops their healing.{' '}
              <span style={{ color: 'var(--gold)', fontWeight: 600 }}>My Tapping Script</span> solves this by giving you the best phrases, specific to you, in just a few clicks.{' '}
              <span style={{ color: 'var(--white)', fontWeight: 600 }}>No more blocks.</span>
            </p>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button className="btn-primary" style={{ width: 'auto', padding: '16px 40px', fontSize: 16 }} onClick={() => setPage('create')}>
              Create Your Free Script
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '80px 0', background: '#0F2240' }}>
        <div className="page-container" style={{ maxWidth: 760 }}>
          <RevealWrap>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 className="section-title">Frequently Asked Questions</h2>
              <div className="divider" style={{ margin: '16px auto' }} />
            </div>
          </RevealWrap>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{
                background: 'var(--navy-card)', border: `1px solid ${openFaq === i ? 'var(--gold-dark)' : 'var(--white-10)'}`,
                borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s',
              }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: '100%', padding: '20px 24px', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--white)', textAlign: 'left', gap: 16,
                  }}>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>{faq.q}</span>
                  <span style={{ color: 'var(--gold)', fontSize: 20, flexShrink: 0, transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0)' }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 24px 20px', borderTop: '1px solid var(--white-10)' }}>
                    <p style={{ fontSize: 15, color: 'var(--white-60)', lineHeight: 1.8, paddingTop: 16 }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT EFT ── */}
      <section style={{ padding: '80px 0', background: 'var(--navy-light)' }}>
        <div className="page-container" style={{ maxWidth: 760 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 className="section-title">What is EFT Tapping?</h2>
            <div className="divider" style={{ margin: '16px auto' }} />
          </div>
          <div className="grid-1-on-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
            {[
              { icon: '🧠', title: 'Clinically Researched', desc: 'EFT has been studied in over 100 clinical trials and shown to significantly reduce cortisol, anxiety, PTSD symptoms, and emotional distress.' },
              { icon: '⚡', title: 'Fast & Effective', desc: 'Most people feel a noticeable shift within a single tapping session. It works by calming the nervous system while you focus on the issue.' },
              { icon: '🫀', title: 'Body & Mind Together', desc: 'EFT bridges the gap between emotional awareness and physical release by tapping on acupressure points while speaking your truth.' },
              { icon: '🌿', title: 'Self-Empowering', desc: 'Unlike therapy that requires a practitioner, EFT is a skill you can use anytime, anywhere. My Tapping Script makes it even easier.' },
            ].map(card => (
              <div key={card.title} className="card" style={{ padding: 28 }}>
                <div style={{ fontSize: 32, marginBottom: 14 }}>{card.icon}</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 10 }}>{card.title}</h3>
                <p style={{ color: 'var(--white-60)', fontSize: 15, lineHeight: 1.7 }}>{card.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button className="btn-secondary" style={{ width: 'auto', padding: '14px 32px' }} onClick={() => setPage('learn')}>
              Learn More About EFT
            </button>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ padding: '80px 0', textAlign: 'center', background: 'radial-gradient(ellipse at 50% 50%, rgba(212,168,67,0.08) 0%, transparent 60%)' }}>
        <div className="page-container" style={{ maxWidth: 600 }}>
          <RevealWrap>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 44px)', marginBottom: 16 }}>
              Your script.<br /><span style={{ color: 'var(--gold)' }}>Your healing.</span>
            </h2>
            <p style={{ color: 'var(--white-60)', marginBottom: 32, fontSize: 16, lineHeight: 1.7 }}>
              Stop struggling to find the words. Start tapping with a script that's made for you, in seconds.
            </p>
            <button className="btn-primary" style={{ width: 'auto', padding: '18px 48px', fontSize: 17 }} onClick={() => setPage('create')}>
              Create My Free Script
            </button>
            <p style={{ marginTop: 16, fontSize: 13, color: 'var(--white-60)' }}>7 free scripts · No credit card needed · Cancel anytime</p>
          </RevealWrap>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--white-10)', padding: '32px 0', color: 'var(--white-60)', fontSize: 13 }}>
        <div className="page-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>© 2026 My Tapping Script</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <span style={{ cursor: 'pointer' }} onClick={() => setOpenPolicy('privacy')}>Privacy Policy</span>
            <span style={{ cursor: 'pointer' }} onClick={() => setOpenPolicy('terms')}>Terms of Service</span>
            <span style={{ cursor: 'pointer' }} onClick={() => setOpenPolicy('disclaimer')}>Disclaimer</span>
            <span style={{ cursor: 'pointer' }} onClick={() => setShowDeleteData(true)}>Delete Data</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
